/**
 * @file chips-choise.component.tsx
 * @description Компонент обертка для работы с @consta/uikit/Chips. Так же реализована интеграция с enum из redux.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-11
 * @dependencies []
 * @related-files []
 */

import { ChipsChoice as ConstaChipsChoice } from '@consta/uikit/Chips';
import { Text } from '@consta/uikit/Text';
import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getItems } from '@features/enums/enum-actions';
import { enumObject, getEnums } from '@features/enums/enum-slice';
import { AppDispatch } from '@store/index';
import cn from 'classnames';
import { TBaseObjectDto } from '@src/types';

/**
 * Пропсы для компонента ChipsChoice
 */
export interface IChipsChoiceProps extends Omit<React.ComponentProps<typeof ConstaChipsChoice>, 'items'> {
    /** Элемент для отображения пустого состояния */
    emptyItem?: TBaseObjectDto | undefined;
    /** Выбранное значение */
    value?: TBaseObjectDto | undefined;
    /** Метка компонента */
    label?: string;
    /** Вид отображения компонента */
    view?: string;
    /** Массив элементов для выбора */
    items?: TBaseObjectDto[];
}

export const ChipsChoice = ({ emptyItem, value, view = 'default', label, items, ...props }: IChipsChoiceProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const enumItems: enumObject[] = useSelector(getEnums);
    const componentItems: TBaseObjectDto[] = useMemo(() => {
        const initialItems = emptyItem ? [emptyItem] : [];

        if (items) {
            return [...initialItems, ...items];
        }

        const currentEnum = enumItems.find((enumItem: enumObject) => enumItem.type === value?.$type);

        if (currentEnum) {
            return [...initialItems, ...currentEnum.items.map((item: TBaseObjectDto) => ({ ...item, label: item.name }))];
        }

        return initialItems;
    }, [enumItems, items]);

    useEffect(() => {
        if (value?.$type && !items && !enumItems?.find((enumItem: enumObject) => enumItem.type === value.$type)) {
            dispatch(getItems({ type: value.$type }));
        }
    }, []);

    return (
        <div className="chips-choice-component flex flex-col gap-[0.4234vmin]">
            {label && (
                <Text size="s" className="whitespace-nowrap dictionary__label font-medium leading-[2.5405vmin]">
                    {label}
                </Text>
            )}

            <ConstaChipsChoice
                getItemKey={(item: TBaseObjectDto) => item.id}
                className={cn(`chips-choice ${view}`)}
                value={value}
                {...props}
                items={componentItems}
            />
        </div>
    );
};
